import { Amplify } from 'aws-amplify'
import { fetchUserAttributes, fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth'

// Amplify設定を確実にするための初期化チェック
const ensureAmplifyConfigured = () => {
  try {
    const config = Amplify.getConfig()
    if (!config?.Auth?.Cognito && window.__amplifyConfig__) {
      // eslint-disable-next-line no-console
      console.log('Re-configuring Amplify in app.js')
      Amplify.configure(window.__amplifyConfig__, { ssr: false })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Amplify configuration check error:', error)
  }
}

export default {
  namespaced: true,
  state: {
    corporation: null,
    cachedDate: null,
    error: null,
    warning: null
  },
  getters: {
    today: () => () => {
      // 毎回算出させるため、メソッドとして実装する
      const d = new Date()
      return d.getFullYear() + '-' + ('00'+(d.getMonth()+1)).slice(-2) + '-'+ ('00'+d.getDate()).slice(-2)
    },
    generateUuid: () => () => {
      // 毎回算出させるため、メソッドとして実装する
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
        .replace(/x/g, () => Math.floor(Math.random() * 16).toString(16))
        .replace(/y/g, () => (Math.floor(Math.random() * 4) + 8).toString(16))
    },
    generateShortId: () => () => {
      // 毎回算出させるため、メソッドとして実装する
      const rnd3char = () => ('000' + (36 ** 3 * Math.random() | 0).toString(36)).slice(-3)
      return rnd3char() + rnd3char()
    },
    cachedCorporation: (state, getters, rootState) => () => {
      // 毎回チェックさせるため、メソッドとして実装する
      if (state.cachedDate == getters.today()) {
        if (state.corporation.corporation_id == rootState.auth.rootCorporationId) {
          // キャッシュ日付が当日 かつ 同一企業ID の場合にキャッシュを返却する
          return state.corporation
        }
      }
      return null
    }
  },
  mutations: {
    cacheCorporation (state, { corporation, today }) {
      state.corporation = corporation
      state.cachedDate = today
    },
    clearCache (state) {
      state.corporation = null
      state.cachedDate = null
    },
    error (state, message) {
      state.error = message
    },
    warning (state, message) {
      state.warning = message
    }
  },
  actions: {
    async authUser({ commit, dispatch, getters }) {
      try {
        // Amplify設定を確認
        ensureAmplifyConfigured()
        
        // v6ではsignIn完了後すぐにこれらのAPIが使える
        const attributes = await fetchUserAttributes()
        const session = await fetchAuthSession()
        
        // セッション検証
        if (!session?.tokens?.idToken) {
          // eslint-disable-next-line no-console
          console.error('Session validation failed', {
            hasSession: !!session,
            hasTokens: !!session?.tokens,
            tokenKeys: session?.tokens ? Object.keys(session.tokens) : []
          })
          return null
        }

        // ユーザー情報の設定
        dispatch('auth/create', { attributes, session }, { root: true })
            
        let corp = getters.cachedCorporation()
        if (!corp) {
          // キャッシュが無い場合
          const corporationId = attributes['custom:corporationId']
          // eslint-disable-next-line no-console
          console.log('Fetching corporation info for ID:', corporationId)
          const params = {'corporation_id': corporationId}
          corp = await dispatch('http/get', { apiName: 'canaryCorp', params: params }, { root: true })
            .then(res => {
              // eslint-disable-next-line no-console
              console.log('Corporation API response:', res)
              return res.data
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Corporation fetch error:', error)
              return null
            })
          if (!corp) {
            // eslint-disable-next-line no-console
            console.error('No corporation data received')
            commit('error', '企業情報の取得に失敗しました。')
            return null
          }
          // eslint-disable-next-line no-console
          console.log('Corporation info retrieved:', corp)
          // 対象の会社情報
          dispatch('auth/switchCorporation', corp, { root: true })

          // キャッシュ時日付も更新するため、取得した場合にのみ設定
          commit('cacheCorporation', { corporation: corp, today: getters.today() })
          commit('warning', null)
          commit('error', null)
        }
      
        const dsec = 24 * 60 * 60 * 1000
        const diffDays = Math.ceil((Date.parse(corp.exp_date) - Date.now()) / dsec)
        if (diffDays < 0) {
          commit('error', '利用期限を過ぎています。販売会社にお問い合わせください。')
          return null
        } else if (diffDays <= 40) {
          commit('warning', '利用期限が近づいています。（残り' + diffDays + '日）')
        }
        return { attributes, session }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Auth user error:', error)
        
        // 特殊なエラーの場合はそのまま伝播
        if (error.name === 'NewPasswordRequiredException' ||
            error.name === 'PasswordResetRequiredException') {
          throw error
        }
        
        // 認証失敗時はエラーメッセージを設定
        if (error.message?.includes('not authenticated') || 
            error.name === 'UserUnAuthenticatedException') {
          commit('error', '認証エラーが発生しました。再度ログインしてください。')
        }
        
        return null
      }
    },
    async signOut({ commit, dispatch }) {
      // エラーと警告をクリア
      commit('error', null)
      commit('warning', null)
      commit('clearCache')
      dispatch('auth/destroy', null, { root: true })
      
      // Amplifyのサインアウトを実行
      await amplifySignOut()
    },
    clearError ({ commit }) {
      commit('error', null)
    },
    clearWarning ({ commit }) {
      commit('warning', null)
    }
  }
}
